/**
Project: Voice Panel (c)
Title: Signin Interactive
Description: Component for displaying the browser sign in page 
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import React, { Component } from "react";
import axios from "axios";
import {
  Button,
  Flex,
  Text,
  Image,
  Loader,
  LinkIcon,
} from "@fluentui/react-northstar";
import IntlTranslate from "../helpers/intl-translate";
import IntlUtil from "../helpers/intl-util";
import { msalInstance, loginRequest } from "../settings/msal-config";
import { authenticateTokenSignin } from "../actions/identity-actions";
import { AppConfigProps } from "../settings/app-config";
import TokenUtil from "../helpers/token-util";
import { AppURLProps } from "../settings/app-urls";
import ResponseUtil from "../helpers/response-util";
import { AppConstants } from "../settings/app-constants";
import SippioVoicePanelLogo from "../../../assets/images/SIPPIO-VoicePanel-Logo.png";
import {
  accessLog,
  accessLogWithToken,
  openConsentPopup,
} from "../actions/common-actions";
import PageUtil from "../helpers/page-util";

class SigninInteractive extends Component {
  _isMounted = false;
  _axiosSource = axios.CancelToken.source();
  _cancelToken = { cancelToken: this._axiosSource.token };
  _intl_ns_common = "common";
  _intl_ns_signin = "signin";
  _clearTimeout = null;

  constructor(props) {
    super(props);

    this.state = {
      authContext: null,
      isFormDataSubmitted: false,
      isSignInButtonDisable: false,
      errorRequestCount: 0,
      errorMessage: null,
      errorDetails: null,
    };
  }

  setStateAsync = (state) => {
    if (this._isMounted) {
      return new Promise((resolve) => {
        this.setState(state, resolve);
      });
    }
  };

  async componentDidMount() {
    this._isMounted = true;
    if (this.props.history.location?.state?.from?.state?.errorMessage) {
      await this.setStateAsync({
        errorMessage:
          this.props.history.location?.state?.from?.state?.errorMessage,
      });
      await this.props.history.replace({
        pathName: AppURLProps.signin,
        state: null,
      });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    clearTimeout(this._clearTimeout);
    this._axiosSource.cancel(
      IntlUtil.getText(
        this._intl_ns_common,
        "notification.warning.requestCancelled"
      )
    );
  }

  handlePopupSignin = () => {
    msalInstance
      .acquireTokenPopup(loginRequest)
      .then(async (res) => {
        msalInstance.setActiveAccount(
          msalInstance.getAccountByHomeId(res.account.homeAccountId)
        );
        await this.handleInteractiveSignin(
          res.idToken,
          this.props.history,
          res.account
        );
      })
      .catch(async (err) => {
        await this.setStateAsync({ errorMessage: err.toString() });
      });
  };

  clearSigninError = async () => {
    await this.setStateAsync({ isFormDataSubmitted: true });
    await this.setStateAsync({ errorMessage: null });
    await this.setStateAsync({ errorDetails: null });
  };

  handleInteractiveSignin = async (token, history, account) => {
    await this.clearSigninError();
    try {
      let res = await authenticateTokenSignin(token);
      if (
        res &&
        res.status === AppConfigProps.httpStatusCode.ok &&
        res.data &&
        res.data.result &&
        res.data.result.accessToken &&
        res.data.result.refreshToken
      ) {
        await TokenUtil.setIdentityToken(
          res.data.result.accessToken,
          res.data.result.refreshToken
        );
        const tokenData = TokenUtil.getIdentitySippioAccessTokenData();
        if (
          tokenData.accountNumber === null ||
          tokenData.accountNumber === ""
        ) {
          let errorMessage = IntlUtil.getText(
            this._intl_ns_signin,
            "notification.error.noAccountId"
          );
          this.setSigninError(errorMessage, null);
          TokenUtil.removeIdentityToken();
        } else if (
          tokenData.regionsTemplate === null ||
          tokenData.regionsTemplate === ""
        ) {
          let errorMessage = IntlUtil.getText(
            this._intl_ns_signin,
            "notification.error.noRegions"
          );
          this.setSigninError(errorMessage, null);
          TokenUtil.removeIdentityToken();
        } else {
          await accessLogWithToken(
            PageUtil.getPageURL(this.props.history.location),
            "UID-SIS",
            "Sign-in Success",
            res.data.result.accessToken
          );
          history.push(AppURLProps.home);
        }
      } else {
        accessLogWithToken(
          PageUtil.getPageURL(this.props.history.location),
          "UID-SIF",
          "Sign-in Failure",
          token
        );
        let errorMessage = IntlUtil.getText(
          this._intl_ns_signin,
          "notification.error.signinIssueUnknown"
        );
        await this.setSigninError(errorMessage, null);
      }
    } catch (err) {
      accessLogWithToken(
        PageUtil.getPageURL(this.props.history.location),
        "UID-SIF",
        "Sign-in Failure",
        token
      );
      msalInstance.logoutRedirect({
        account: msalInstance.getActiveAccount(),
        onRedirectNavigate: (url) => {
          return false;
        },
      });
      let errorMessage = IntlUtil.getText(
        this._intl_ns_signin,
        "notification.error.signinIssueUnknown"
      );
      await this.setSigninError(errorMessage, null);
      let errorDetails = [];
      if (err && err.status) {
        if (err.status === AppConfigProps.httpStatusCode.badRequest) {
          errorMessage = IntlUtil.getText(
            this._intl_ns_signin,
            "notification.error.signinIssueUnknown"
          );
          await this.setSigninError(errorMessage, null);
        } else if (err.status === AppConfigProps.httpStatusCode.forbidden) {
          const primeError = ResponseUtil.getPrimeError(err);
          if (
            primeError &&
            primeError.code ===
              AppConstants.SIGIN_IN.FORBIDDEN_ERROR_MESSAGES.ADMIN_PRIVILEGES
          ) {
            errorMessage =
              account.username +
              ", " +
              IntlUtil.getText(
                this._intl_ns_signin,
                "notification.error.privilegeError"
              );
            await this.setSigninError(errorMessage, null);
          } else if (
            primeError &&
            primeError.code ===
              AppConstants.SIGIN_IN.FORBIDDEN_ERROR_MESSAGES.ADMIN_CONSENT
          ) {
            errorMessage = IntlUtil.getText(
              this._intl_ns_signin,
              "notification.error.signinNoConsent"
            );
            if (primeError.message && primeError.message.trim().length > 0) {
              errorDetails.push(primeError.message);
            }
            await this.setSigninError(errorMessage, errorDetails);
          } else if (
            primeError &&
            primeError.code ===
              AppConstants.SIGIN_IN.FORBIDDEN_ERROR_MESSAGES.LYNC_RESOURCE
          ) {
            errorMessage = IntlUtil.getText(
              this._intl_ns_signin,
              "notification.error.signinNoLync"
            );
            if (primeError.message && primeError.message.trim().length > 0) {
              errorDetails.push(primeError.message);
            }
            await this.setSigninError(errorMessage, errorDetails);
          } else if (
            primeError &&
            primeError.code ===
              AppConstants.SIGIN_IN.FORBIDDEN_ERROR_MESSAGES.SIGNIN_DIRECTORY
          ) {
            let consent = JSON.parse(primeError.message);
            if (
              consent &&
              consent.error &&
              (consent.error.code?.toLowerCase() ===
                AppConstants.MICROSOFT_ERROR_CODE.AUTHORIZATiON_IDENTITY_NOT_FOUND.toLowerCase() ||
                consent.error.code?.toLowerCase() ===
                  AppConstants.MICROSOFT_ERROR_CODE.AUTHORIZATiON_REQUEST_DENIED.toLowerCase())
            ) {
              if (this.state.errorRequestCount <= 1) {
                let errorRequestCount = this.state.errorRequestCount;
                await this.setStateAsync({
                  errorRequestCount: ++errorRequestCount,
                });
                errorMessage = IntlUtil.getText(
                  this._intl_ns_signin,
                  "notification.error.signinPleaseWaitConsent"
                );
                await this.setStateAsync({
                  errorMessage: errorMessage,
                  isSignInButtonDisable: true,
                });
                this._clearTimeout = setTimeout(async () => {
                  await this.setStateAsync({
                    errorMessage: null,
                    isSignInButtonDisable: false,
                  });
                }, 20000);
                this.setSigninError(errorMessage, errorDetails);
                sessionStorage.clear();
              } else {
                errorMessage = IntlUtil.getText(
                  this._intl_ns_signin,
                  "notification.error.signinNoDirectory"
                );
                if (
                  primeError.message &&
                  primeError.message.trim().length > 0
                ) {
                  errorDetails.push(primeError.message);
                }
                await this.setSigninError(errorMessage, errorDetails);
              }
            } else {
              errorMessage = IntlUtil.getText(
                this._intl_ns_signin,
                "notification.error.signinNoDirectory"
              );
              if (primeError.message && primeError.message.trim().length > 0) {
                errorDetails.push(primeError.message);
              }
              await this.setSigninError(errorMessage, errorDetails);
            }
          } else {
            errorMessage = IntlUtil.getText(
              this._intl_ns_signin,
              "notification.error.signinError"
            );
            await this.setSigninError(errorMessage, null);
          }
        } else {
          errorMessage = IntlUtil.getText(
            this._intl_ns_signin,
            "notification.error.signinIssueUnknown"
          );
          await this.setSigninError(errorMessage, null);
        }
      }
      this.setSigninError(errorMessage, errorDetails);
    }
  };

  setSigninError = async (errorMessage, errorDetails) => {
    await this.setStateAsync({ isFormDataSubmitted: false });
    await this.setStateAsync({ errorMessage: errorMessage });
    if (errorDetails && errorDetails.length > 0) {
      await this.setStateAsync({ errorDetails: errorDetails });
    }
  };

  consentLinkClick = async () => {
    await openConsentPopup();
  };

  render() {
    return (
      <>
        <Flex
          gap="gap.small"
          hAlign="center"
          vAlign="center"
          className="signin-page-wrapper"
          column
        >
          <Flex column gap="gap.large" className="signin-silent-wrapper">
            <Flex column gap="gap.medium" hAlign="center">
              <Image
                src={SippioVoicePanelLogo}
                alt={IntlUtil.getText(
                  this._intl_ns_common,
                  "content.voicePanelBySippio"
                )}
                title={IntlUtil.getText(
                  this._intl_ns_common,
                  "content.voicePanelBySippio"
                )}
              />
            </Flex>
            <Flex column gap="gap.small" hAlign="start">
              <Flex column hAlign="start">
                <Text
                  content={IntlUtil.getText(
                    this._intl_ns_signin,
                    "content.teamsSigninInfo1"
                  )}
                  className="signin-text"
                />
              </Flex>
              <Flex column hAlign="start">
                <Text
                  content={IntlUtil.getText(
                    this._intl_ns_signin,
                    "content.teamsSigninInfo2"
                  )}
                  className="signin-text"
                />
              </Flex>
              <div>
                <Text
                  content={IntlUtil.getText(
                    this._intl_ns_signin,
                    "content.note"
                  )}
                  className="signin-text text-bold"
                />
                <Text
                  content={IntlUtil.getText(
                    this._intl_ns_signin,
                    "content.teamsSigninInfo4"
                  )}
                  className="signin-text"
                />
                <span className="text-link" onClick={this.consentLinkClick}>
                  {IntlUtil.getText(
                    this._intl_ns_signin,
                    "content.acceptConsent"
                  )}
                </span>
                <Text
                  content={IntlUtil.getText(
                    this._intl_ns_signin,
                    "content.teamsSigninInfo5"
                  )}
                  className="signin-text"
                />
              </div>
            </Flex>
            {this.state.errorMessage ? (
              <Flex column gap="gap.medium" hAlign="start">
                {this.state.errorMessage &&
                this.state.isSignInButtonDisable === true ? (
                  <Loader
                    className="page-loader-consent-signin signin-div"
                    size="smallest"
                    labelPosition="end"
                    inline={true}
                    label={this.state.errorMessage}
                  />
                ) : (
                  <Text
                    content={this.state.errorMessage}
                    error={true}
                    className="signin-text"
                  />
                )}
                {this.state.errorDetails &&
                this.state.errorDetails.length > 0 ? (
                  <Flex column gap="gap.small" className="signin-div">
                    {this.state.errorDetails.map((error, index) => {
                      return (
                        <Text key={`error-details-${index}`} content={error} />
                      );
                    })}
                  </Flex>
                ) : null}
              </Flex>
            ) : null}
            <Flex column gap="gap.medium" hAlign="center">
              {this.state.isFormDataSubmitted ? (
                <Button type="submit" primary className="signin-button-animate">
                  <span className="signin-button-text">
                    <IntlTranslate
                      namespace={this._intl_ns_signin}
                      keyId="content.signin"
                    />
                  </span>
                  <span className="signin-button-fadebar" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  content={
                    <IntlTranslate
                      namespace={this._intl_ns_signin}
                      keyId="content.signin"
                    />
                  }
                  primary
                  disabled={this.state.isSignInButtonDisable}
                  className="signin-button"
                  onClick={() => {
                    clearTimeout(this._clearTimeout);
                    sessionStorage.clear();
                    this.setStateAsync({
                      errorMessage: null,
                      errorDetails: null,
                    });
                    this.handlePopupSignin();
                  }}
                />
              )}
            </Flex>
          </Flex>
        </Flex>
      </>
    );
  }
}
export default SigninInteractive;
